import { ChangeDetectionStrategy, Component, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SzBottomSheetComponent, SzButtonComponent } from '@ui';
import { AppRatingService } from '../app-rating.service';

@Component({
  selector: 'lib-rating-prompt-sheet',
  templateUrl: './rating-prompt-sheet.component.html',
  styleUrls: ['./rating-prompt-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, SzBottomSheetComponent, SzButtonComponent]
})
export class RatingPromptSheetComponent {
  private readonly ratingService: AppRatingService = inject(AppRatingService);

  readonly open: Signal<boolean> = this.ratingService.open;
  readonly stars: ReadonlyArray<number> = [1, 2, 3, 4, 5];

  readonly selected: WritableSignal<number> = signal<number>(0);
  readonly comment: WritableSignal<string> = signal<string>('');
  readonly submitting: WritableSignal<boolean> = signal<boolean>(false);

  readonly showComment: Signal<boolean> = computed((): boolean => {
    const s: number = this.selected();
    return s >= 1 && s < 5;
  });

  select(value: number): void {
    this.selected.set(value);
  }

  onCommentInput(event: Event): void {
    this.comment.set((event.target as HTMLTextAreaElement).value);
  }

  async onSubmit(): Promise<void> {
    const rating: number = this.selected();
    if (rating < 1 || this.submitting()) return;
    this.submitting.set(true);
    try {
      await this.ratingService.submit(rating, this.showComment() ? this.comment() : null);
      this.reset();
    } finally {
      this.submitting.set(false);
    }
  }

  onDismiss(): void {
    this.ratingService.dismiss();
    this.reset();
  }

  private reset(): void {
    this.selected.set(0);
    this.comment.set('');
  }
}
